import { ChangeDetectionStrategy, Component, ElementRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { BaseDestroyableComponent } from '@shared/base-classes/base-destroyable';
import { TranslateModule } from '@ngx-translate/core';
import { CrownAiBannerVisibilityService } from '@shared/services/crown-ai-banner-visibility.service';
import { RxIntuitiveToothEditorComponent } from '../../components/rx-intuitive-tooth-editor/rx-intuitive-tooth-editor.component';
import { RxIntuitiveTeethDiagramComponent } from '../../components/rx-intuitive-teeth-diagram/rx-intuitive-teeth-diagram.component';
import { RxIntuitiveTeethTreatmentViewlistComponent } from '../../components/rx-intuitive-teeth-treatment-viewlist/rx-intuitive-teeth-treatment-viewlist.component';
import { CrownForm } from '@modules/rx-intuitive-crown/models/crown-form';
import { AdditionalInfoResult } from '@modules/rx-intuitive-crown-additional-info/models/additional-info';
import { RxIntuitiveIndicationMapperService } from '@modules/rx-intuitive-teeth-orchestrator/services/rx-intuitive-indication-mapper.service';
import { RxIntuitiveToothEditorFacade } from '../../rx-intuitive-tooth-editor.facade';

@Component({
	selector: 'rx-intuitive-tooth-editor-container',
	templateUrl: './rx-intuitive-tooth-editor-container.component.html',
	styleUrls: ['./rx-intuitive-tooth-editor-container.component.scss'],
	standalone: true,
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [CommonModule, TranslateModule, RxIntuitiveToothEditorComponent, RxIntuitiveTeethTreatmentViewlistComponent]
})
export class RxIntuitiveToothEditorContainerComponent extends BaseDestroyableComponent {
	@ViewChild(RxIntuitiveTeethDiagramComponent, { read: ElementRef }) private readonly teethDiagram?: ElementRef<HTMLElement>;

	readonly copyFromMenuDefaultIndicationId: number;

	readonly previewTreatmentListItems$ = this.toothEditorFacade.previewTreatmentListItems$;
	readonly editorTeeth$ = this.toothEditorFacade.editorTeeth$;
	readonly isToothEditorShown$ = this.toothEditorFacade.isToothEditorShown$;
	readonly toothEditorReadOnly$ = this.toothEditorFacade.toothEditorReadOnly$;
	readonly canDeleteToothEditor$: Observable<boolean> = this.toothEditorFacade.canDeleteToothEditor$;
	readonly canEditToothEditor$: Observable<boolean> = this.toothEditorFacade.canEditToothEditor$;

	/** Renders the treatment list when there are preview teeth but the side editor is hidden. */
	readonly showTreatmentViewList$ = combineLatest([
		this.toothEditorFacade.previewTreatmentListItems$,
		this.toothEditorFacade.isToothEditorShown$
	]).pipe(map(([items, editorShown]) => items.length > 0 && !editorShown));

	readonly showSidePanelPlaceholder$ = combineLatest([
		this.toothEditorFacade.previewTreatmentListItems$,
		this.toothEditorFacade.isToothEditorShown$
	]).pipe(map(([items, editorShown]) => !(items.length > 0 || editorShown)));

	readonly isCrownAIBannerVisible$ = this.crownAiBannerVisibilityService.isVisible$;

	constructor(
		protected readonly toothEditorFacade: RxIntuitiveToothEditorFacade,
		private readonly crownAiBannerVisibilityService: CrownAiBannerVisibilityService,
		private readonly indicationMapper: RxIntuitiveIndicationMapperService
	) {
		super();
		this.copyFromMenuDefaultIndicationId = indicationMapper.getDefaultIndication();
	}

	onResetSelectedRequested(): void {
		this.toothEditorFacade.resetIndicationForSelectedTeeth();
	}

	onOpenTreatmentEditorFromList(toothId: number): void {
		this.toothEditorFacade.openTreatmentEditorFromList(toothId);
	}

	clearSelection(): void {
		this.toothEditorFacade.resetIndicationForSelectedTeeth();
	}

	onToothCrownFormChanged(crownForm: CrownForm): void {
		this.toothEditorFacade.updateToothCrownData(crownForm);
	}

	onToothAdditionalInfoApplied(event: { toothIds: number[]; result: AdditionalInfoResult }): void {
		this.toothEditorFacade.applyToothAdditionalInfo(event.toothIds, event.result);
	}

	onToothClearDisabledFields(event: { toothIds: number[]; payload: Partial<Record<string, unknown>> }): void {
		this.toothEditorFacade.clearDisabledToothFields(event.toothIds, event.payload);
	}
}
